import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, ChevronRight, Container, Link2, Radio, ShieldAlert, Truck } from 'lucide-react';
import MoverRegistryPanel from './fleet/MoverRegistryPanel';
import AttachmentRegistryPanel from './fleet/AttachmentRegistryPanel';
import VirtualCouplingStudio from './fleet/VirtualCouplingStudio';
import MoverModal from './fleet/MoverModal';
import AttachmentModal from './fleet/AttachmentModal';
import DashcamFeedModal from './fleet/DashcamFeedModal';
import { parseDashcamDeepLink } from './fleet/dashcamPairingUtils';
import {
  buildAttachmentFromForm,
  buildMoverFromForm,
  EMPTY_ATTACHMENT_FORM,
  EMPTY_MOVER_FORM,
  attachmentToForm,
  generateAttachmentId,
  generateCouplingId,
  generateMoverId,
  MOCK_ACTIVE_COUPLINGS,
  MOCK_ATTACHMENTS,
  MOCK_MOVERS,
  getFleetComplianceSummary,
  moverToForm,
  simulateAssetDoc,
  simulateMoverPhoto,
  simulateUnitPhoto,
} from './fleet/fleetCouplingData';

const INTERNAL_TABS = [
  { id: 'coupling', label: 'Virtual Coupling Studio', sublabel: 'Hook · Detach · Dashcam', icon: Link2 },
  { id: 'movers', label: 'Movers Registry', sublabel: 'Truck heads · Dashcam', icon: Truck },
  { id: 'attachments', label: 'Attachments Registry', sublabel: 'Trailers · Tankers · ISO', icon: Container },
];

export default function FleetManagement() {
  const [activeTab, setActiveTab] = useState('coupling');

  const [movers, setMovers] = useState(MOCK_MOVERS);
  const [attachments, setAttachments] = useState(MOCK_ATTACHMENTS);
  const [activeCouplings, setActiveCouplings] = useState(MOCK_ACTIVE_COUPLINGS);

  const [selectedMoverId, setSelectedMoverId] = useState('');
  const [selectedAttachmentId, setSelectedAttachmentId] = useState('');

  const [isMoverModalOpen, setIsMoverModalOpen] = useState(false);
  const [editingMover, setEditingMover] = useState(null);
  const [moverForm, setMoverForm] = useState(EMPTY_MOVER_FORM);

  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);
  const [editingAttachment, setEditingAttachment] = useState(null);
  const [attachmentForm, setAttachmentForm] = useState(EMPTY_ATTACHMENT_FORM);

  const [dashcamCoupling, setDashcamCoupling] = useState(null);
  const [directDashcamMoverId, setDirectDashcamMoverId] = useState(null);
  const [toastMsg, setToastMsg] = useState('');

  useEffect(() => {
    const { dash, moverId } = parseDashcamDeepLink();
    if (!dash || !moverId) return;

    setActiveTab('coupling');

    const coupled = MOCK_ACTIVE_COUPLINGS.find((c) => c.moverId === moverId);
    if (coupled) {
      setDashcamCoupling(coupled);
      return;
    }

    const moverExists = MOCK_MOVERS.some((m) => m.id === moverId);
    if (moverExists) setDirectDashcamMoverId(moverId);
  }, []);

  const showToast = (msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(''), 3500);
  };

  const openAddMover = () => {
    setEditingMover(null);
    setMoverForm({ ...EMPTY_MOVER_FORM });
    setIsMoverModalOpen(true);
  };

  const openEditMover = (mover) => {
    setEditingMover(mover);
    setMoverForm(moverToForm(mover));
    setIsMoverModalOpen(true);
  };

  const closeMoverModal = () => {
    setIsMoverModalOpen(false);
    setEditingMover(null);
    setMoverForm({ ...EMPTY_MOVER_FORM });
  };

  const openAddAttachment = () => {
    setEditingAttachment(null);
    setAttachmentForm({ ...EMPTY_ATTACHMENT_FORM });
    setIsAttachmentModalOpen(true);
  };

  const openEditAttachment = (attachment) => {
    setEditingAttachment(attachment);
    setAttachmentForm(attachmentToForm(attachment));
    setIsAttachmentModalOpen(true);
  };

  const closeAttachmentModal = () => {
    setIsAttachmentModalOpen(false);
    setEditingAttachment(null);
    setAttachmentForm({ ...EMPTY_ATTACHMENT_FORM });
  };

  const handleSaveMover = (e) => {
    e.preventDefault();
    if (!moverForm.plateNumber.trim()) return;

    if (editingMover) {
      const updated = buildMoverFromForm(moverForm, editingMover.id);
      setMovers((prev) => prev.map((m) => (m.id === editingMover.id ? updated : m)));
      showToast(`${updated.plateNumber} updated.`);
    } else {
      const newMover = buildMoverFromForm(moverForm, generateMoverId(movers));
      setMovers((prev) => [newMover, ...prev]);
      showToast(`${newMover.plateNumber} registered as mover head.`);
    }
    closeMoverModal();
  };

  const handleSaveAttachment = (e) => {
    e.preventDefault();
    if (!attachmentForm.type || !attachmentForm.serialRegistrationNo.trim()) return;

    if (editingAttachment) {
      const updated = buildAttachmentFromForm(attachmentForm, editingAttachment.id);
      setAttachments((prev) => prev.map((a) => (a.id === editingAttachment.id ? updated : a)));
      showToast(`${updated.serialRegistrationNo} updated.`);
    } else {
      const newUnit = buildAttachmentFromForm(attachmentForm, generateAttachmentId(attachments));
      setAttachments((prev) => [newUnit, ...prev]);
      showToast(`${newUnit.serialRegistrationNo} registered.`);
    }
    closeAttachmentModal();
  };

  const handleCouple = () => {
    if (!selectedMoverId || !selectedAttachmentId) return;

    const mover = movers.find((m) => m.id === selectedMoverId);
    const attachment = attachments.find((a) => a.id === selectedAttachmentId);
    if (!mover || !attachment || mover.status !== 'Available' || attachment.status !== 'Available') return;

    const coupling = {
      id: generateCouplingId(activeCouplings),
      moverId: selectedMoverId,
      attachmentId: selectedAttachmentId,
      coupledAt: new Date().toISOString(),
    };

    setActiveCouplings((prev) => [coupling, ...prev]);
    setMovers((prev) => prev.map((m) => (m.id === selectedMoverId ? { ...m, status: 'Engaged' } : m)));
    setAttachments((prev) => prev.map((a) => (a.id === selectedAttachmentId ? { ...a, status: 'Engaged' } : a)));
    setSelectedMoverId('');
    setSelectedAttachmentId('');
    showToast(`${mover.plateNumber} + ${attachment.serialRegistrationNo} coupled successfully.`);
  };

  const handleDetach = (couplingId) => {
    const coupling = activeCouplings.find((c) => c.id === couplingId);
    if (!coupling) return;

    setActiveCouplings((prev) => prev.filter((c) => c.id !== couplingId));
    setMovers((prev) =>
      prev.map((m) => (m.id === coupling.moverId ? { ...m, status: 'Available' } : m)),
    );
    setAttachments((prev) =>
      prev.map((a) => (a.id === coupling.attachmentId ? { ...a, status: 'Available' } : a)),
    );

    const mover = movers.find((m) => m.id === coupling.moverId);
    const attachment = attachments.find((a) => a.id === coupling.attachmentId);
    showToast(`${mover?.plateNumber} ↔ ${attachment?.serialRegistrationNo} detached.`);
  };

  const handleOpenDashcam = (coupling) => {
    setDirectDashcamMoverId(null);
    setDashcamCoupling(coupling);
  };

  const dashcamMoverId = dashcamCoupling?.moverId || directDashcamMoverId;
  const dashcamMover = dashcamMoverId
    ? movers.find((m) => m.id === dashcamMoverId)
    : null;
  const isDashcamOpen = Boolean(dashcamMover);

  const entityIdForUpload = editingMover?.id || 'draft-mover';
  const attachmentIdForUpload = editingAttachment?.id || 'draft-attachment';

  const compliance = useMemo(
    () => getFleetComplianceSummary(movers, attachments),
    [movers, attachments],
  );

  return (
    <section aria-label="Fleet management" className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-logi-grid pb-4">
        <div>
          <div className="mb-2 flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-logi-accent/10 ring-1 ring-logi-accent/30">
              <Truck className="h-4 w-4 text-logi-accent" strokeWidth={1.75} />
            </div>
            <span className="text-xs font-semibold uppercase tracking-widest text-logi-accent/80">
              OGTS LogiCore™ · Decoupled Fleet Architecture
            </span>
          </div>
          <h2 className="text-xl font-bold tracking-tight text-logi-text sm:text-2xl">
            Fleet Management
          </h2>
          <p className="mt-1 text-sm text-logi-muted">
            Movers &amp; attachments · Virtual coupling studio · AI dashcam feeds
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {toastMsg && (
            <span className="flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700">
              <CheckCircle2 className="h-3.5 w-3.5" />
              {toastMsg}
            </span>
          )}
          <div className="rounded-xl border border-logi-accent/25 bg-logi-accent/10 px-3 py-2 text-center">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-logi-text0">Coupled</p>
            <p className="font-mono text-lg font-bold text-logi-accent">{activeCouplings.length}</p>
          </div>
          <div className="rounded-xl border border-logi-grid bg-white px-3 py-2 text-center">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-logi-text0">Movers</p>
            <p className="font-mono text-lg font-bold text-logi-text">{movers.length}</p>
          </div>
          <div className="rounded-xl border border-logi-accent/30 bg-logi-accent/10 px-3 py-2 text-center">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-logi-text0">Attachments</p>
            <p className="font-mono text-lg font-bold text-logi-accent">{attachments.length}</p>
          </div>
          <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-center">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-logi-text0">Critical</p>
            <p className="font-mono text-lg font-bold text-red-700">{compliance.critical}</p>
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-center">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-logi-text0">Warning</p>
            <p className="font-mono text-lg font-bold text-amber-700">{compliance.warning}</p>
          </div>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-center">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-logi-text0">Valid</p>
            <p className="font-mono text-lg font-bold text-emerald-700">{compliance.valid}</p>
          </div>
        </div>
      </div>

      {compliance.critical > 0 && (
        <section
          aria-label="Critical compliance alerts"
          className="overflow-hidden rounded-xl border border-red-200 bg-gradient-to-r from-red-50 via-white to-red-50/80 ring-1 ring-red-500/20"
        >
          <div className="flex flex-wrap items-center gap-3 border-b border-red-500/20 px-4 py-2.5">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-red-500/20 ring-1 ring-red-500/40">
                <ShieldAlert className="h-4 w-4 animate-pulse text-red-700" strokeWidth={1.75} />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-black">
                  Critical Compliance Alerts
                </p>
                <p className="text-[10px] text-black/70">
                  {compliance.critical} critical · action required within 7 days
                </p>
              </div>
            </div>
            <span className="inline-flex items-center gap-1 rounded-full bg-red-500/15 px-2 py-0.5 text-[10px] font-bold uppercase text-black ring-1 ring-red-500/30">
              <Radio className="h-2.5 w-2.5 animate-pulse" />
              Fleet Compliance Feed
            </span>
          </div>

          <div className="divide-y divide-red-500/10">
            {compliance.criticalItems.map((item) => (
              <div
                key={`${item.label}-${item.dateStr}`}
                className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 transition-colors hover:bg-red-500/[0.04]"
              >
                <div className="flex min-w-0 flex-1 items-start gap-2.5">
                  <AlertTriangle
                    className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-700"
                    strokeWidth={1.75}
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-black">{item.label}</p>
                    <p className="mt-0.5 font-mono text-[10px] text-black/70">
                      {item.days < 0 ? 'EXPIRED' : `${item.days} days remaining`} · {item.dateStr}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  className="inline-flex flex-shrink-0 items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide text-black transition hover:bg-red-500/20"
                >
                  Review
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {compliance.warning > 0 && compliance.critical === 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5">
          <p className="flex items-center gap-2 text-xs font-semibold text-amber-700">
            <AlertTriangle className="h-4 w-4" />
            {compliance.warning} compliance item{compliance.warning > 1 ? 's' : ''} expiring within 60 days
          </p>
        </div>
      )}

      <div className="flex gap-1 rounded-xl border border-logi-grid bg-white p-1">
        {INTERNAL_TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex flex-1 items-center gap-2.5 rounded-lg px-4 py-2.5 text-left transition-all ${
                isActive ? 'bg-logi-accent/10 text-logi-accent ring-1 ring-logi-accent/30' : 'text-logi-muted hover:bg-logi-silver/80 hover:text-logi-text'
              }`}
            >
              <Icon className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-logi-accent' : 'text-logi-text0'}`} strokeWidth={1.75} />
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold">{tab.label}</p>
                <p className="truncate text-[10px] text-logi-text0">{tab.sublabel}</p>
              </div>
            </button>
          );
        })}
      </div>

      <div key={activeTab} className="animate-[fadeSlideIn_0.25s_ease-out]">
        {activeTab === 'coupling' && (
          <VirtualCouplingStudio
            movers={movers}
            attachments={attachments}
            activeCouplings={activeCouplings}
            selectedMoverId={selectedMoverId}
            selectedAttachmentId={selectedAttachmentId}
            onSelectMover={setSelectedMoverId}
            onSelectAttachment={setSelectedAttachmentId}
            onCouple={handleCouple}
            onDetach={handleDetach}
            onOpenDashcam={handleOpenDashcam}
          />
        )}
        {activeTab === 'movers' && (
          <MoverRegistryPanel movers={movers} onAdd={openAddMover} onEdit={openEditMover} />
        )}
        {activeTab === 'attachments' && (
          <AttachmentRegistryPanel attachments={attachments} onAdd={openAddAttachment} onEdit={openEditAttachment} />
        )}
      </div>

      <MoverModal
        isOpen={isMoverModalOpen}
        isEditing={Boolean(editingMover)}
        form={moverForm}
        onFieldChange={(field, value) => setMoverForm((prev) => ({ ...prev, [field]: value }))}
        onPhotoUpload={(file) =>
          setMoverForm((prev) => ({
            ...prev,
            moverPhoto: simulateMoverPhoto(file, entityIdForUpload),
          }))
        }
        onDocUpload={(docKey, file) =>
          setMoverForm((prev) => ({
            ...prev,
            [docKey]: simulateAssetDoc(file, entityIdForUpload, docKey),
          }))
        }
        onSave={handleSaveMover}
        onClose={closeMoverModal}
      />

      <AttachmentModal
        isOpen={isAttachmentModalOpen}
        isEditing={Boolean(editingAttachment)}
        form={attachmentForm}
        onFieldChange={(field, value) => setAttachmentForm((prev) => ({ ...prev, [field]: value }))}
        onPhotoUpload={(file) =>
          setAttachmentForm((prev) => ({
            ...prev,
            unitPhoto: simulateUnitPhoto(file, attachmentIdForUpload),
          }))
        }
        onDocUpload={(docKey, file) =>
          setAttachmentForm((prev) => ({
            ...prev,
            [docKey]: simulateAssetDoc(file, attachmentIdForUpload, docKey),
          }))
        }
        onSave={handleSaveAttachment}
        onClose={closeAttachmentModal}
      />

      <DashcamFeedModal
        coupling={dashcamCoupling}
        mover={dashcamMover}
        isOpen={isDashcamOpen}
        onClose={() => {
          setDashcamCoupling(null);
          setDirectDashcamMoverId(null);
        }}
      />
    </section>
  );
}
